import React, { useEffect, useRef } from "react";
import ReactGA from "react-ga4";

import { forceCheck } from "react-lazyload";

// Types
import { TypeTagBase } from "src/types/contentfulTypes";

// Hooks
import { useTags } from "src/hooks/useTags";
import { useFilteredTags } from "src/hooks/useFilteredTags";

// Components
import { Multiselect } from "multiselect-react-dropdown";

// Styles
import "./RecipeFilters.scss";
import { recipeFiltersInputOptions } from "src/components/molecules/recipeFilters/recipeFiltersInputOptions";

const fieldId = "tags-filter";

type FilterProps = TypeTagBase[] | [];

interface RecipeFiltersProps {
  filteredTags: FilterProps;
  searchInputValue: string | null;
  setSearchInputValue: React.Dispatch<React.SetStateAction<string | null>>;
}

const RecipeFilters = ({
  // tags,
  // tagsFiltered,
  // storedFilteredTags,
  // recipesFiltered,
  filteredTags,
  searchInputValue,
  setSearchInputValue,
}: RecipeFiltersProps) => {
  const { setFilteredTags } = useFilteredTags();

  const multiDropdownRef = useRef<any>(null);
  // The multiselect keeps its own private input state, so we mirror the latest
  // external search term to avoid stale restores after blur.
  const latestSearchInputValueRef = useRef<string | null>(searchInputValue);

  // Filtered tags
  const { data: allTags } = useTags();

  // useEffect(() => {
  //   // recipesFiltered(searchTerm?.toLowerCase());
  // }, [searchTerm]);

  useEffect(() => {
    //  hack to refresh images that came into the viewport
    setTimeout(function () {
      forceCheck();
    }, 500);
  }, [filteredTags, searchInputValue]);

  useEffect(() => {
    latestSearchInputValueRef.current = searchInputValue;
    multiDropdownRef.current?.setState({
      // Keep the third-party input aligned with our search state so clears and
      // route resets are reflected in the visible textbox.
      inputValue: searchInputValue ?? "",
    });
  }, [searchInputValue]);

  const onChange = (inputtedTags: FilterProps) => {
    const storedTagSlugs = filteredTags.map((tag) => tag.slug);
    const actualFilterUsed = inputtedTags.find(
      (tag) => !storedTagSlugs.includes(tag.slug)
    );

    if (actualFilterUsed?.slug) {
      ReactGA.event({
        category: "Filters",
        action: "Filter added",
        label: actualFilterUsed.slug,
      });
    }

    setFilters(inputtedTags);
    setSearchInputValue(null);
  };

  const onRemove = (inputtedTags: FilterProps) => {
    ReactGA.event({
      category: "Filters",
      action: "Single filter removed",
    });

    setFilters(inputtedTags);
  };

  const onSearch = (searchTerm: string) => {
    setSearchInputValue(searchTerm);
  };

  const onBlurCapture = () => {
    window.setTimeout(() => {
      multiDropdownRef.current?.setState({
        // The library clears its input on blur, but we only want that after a
        // deliberate reset such as selecting a tag or clearing filters.
        inputValue: latestSearchInputValueRef.current ?? "",
      });
    }, 300);
  };

  const onClear = () => {
    setFilters([]);
    setSearchInputValue(null);
    ReactGA.event({
      category: "Filters",
      action: "All filters reset",
    });
  };

  const setFilters = (inputtedTags: FilterProps) => {
    setFilteredTags(inputtedTags);
    //   setFilteredTags(inputtedTags);
    //   tagsFiltered(inputtedTags);

    document.getElementById("tags-filter_input")?.blur();
  };

  return (
    <div
      className="recipe-filters__container"
      onBlurCapture={onBlurCapture}
    >
      <label
        className="recipe-filters__label visually-hidden"
        htmlFor={`${fieldId}_input`}
      >
        Filter recipes
      </label>

      <Multiselect
        ref={multiDropdownRef}
        id={fieldId}
        closeOnSelect={true}
        options={allTags}
        selectedValues={filteredTags}
        showCheckbox={true}
        onSelect={onChange}
        onRemove={onRemove}
        onSearch={onSearch}
        displayValue="title"
        groupBy="type"
        placeholder="Filter recipes"
        closeIcon="cancel"
        emptyRecordMsg="No tags found"
        // Preserve the search text while typing so it can double as a recipe
        // name filter until a tag is selected.
        keepSearchTerm={true}
        style={recipeFiltersInputOptions}
      />
      {filteredTags.length ? (
        <button
          className="link-button recipe-filters__link"
          onClick={() => onClear()}
        >
          Clear filter{`${filteredTags.length > 1 ? "s" : ""}`}
        </button>
      ) : (
        ""
      )}
    </div>
  );
};

export default RecipeFilters;
